import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, MessageSquare, Save, Loader2, AlertTriangle, Clock, PlayCircle, Check, X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AdminSidebar from "../../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section 18 Rev 2 — Minimal admin UI for the existing trial reminder settings.
 *
 * Reads and updates:  GET/PUT /api/admin/trial-reminders/settings
 * Reads channel state: GET     /api/admin/trial-reminders/status
 * Manual run:          POST    /api/admin/trial-reminders/run
 *
 * The reminder engine (cadence, dispatch loop, idempotency) is untouched.
 */
export default function AdminTrialReminderSettings() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [emails, setEmails] = useState("");
  const [phones, setPhones] = useState("");
  const [offsets, setOffsets] = useState("7, 2");
  const [sendHour, setSendHour] = useState(9);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken")}` });

  const loadAll = async () => {
    try {
      const [s, st] = await Promise.all([
        axios.get(`${API}/admin/trial-reminders/settings`, { headers: headers() }),
        axios.get(`${API}/admin/trial-reminders/status`, { headers: headers() }),
      ]);
      setSettings(s.data);
      setStatus(st.data);
      setEmails((s.data.admin_emails || []).join(", "));
      setPhones((s.data.admin_phones || []).join(", "));
      setOffsets((s.data.offsets_days || [7, 2]).join(", "));
      setSendHour(s.data.send_hour_local ?? 9);
      setEnabled(s.data.enabled !== false);
    } catch (e) {
      toast.error("Failed to load reminder settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const parseList = (raw) => raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const parsedEmails = parseList(emails);
      const parsedPhones = parseList(phones);
      const parsedOffsets = parseList(offsets).map(x => parseInt(x, 10)).filter(n => n > 0 && n <= 60);
      if (!parsedOffsets.length) {
        toast.error("Add at least one reminder offset (e.g. 7, 2)");
        setSaving(false); return;
      }
      const hour = Number(sendHour);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        toast.error("Send hour must be between 0 and 23");
        setSaving(false); return;
      }
      const body = {
        admin_emails: parsedEmails,
        admin_phones: parsedPhones,
        offsets_days: parsedOffsets,
        send_hour_local: hour,
        enabled,
      };
      const r = await axios.put(`${API}/admin/trial-reminders/settings`, body, { headers: headers() });
      setSettings(r.data);
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setDispatching(true);
    try {
      const r = await axios.post(`${API}/admin/trial-reminders/run`, {}, { headers: headers() });
      const { seeded = 0, sent = 0, failed = 0, no_response = 0 } = r.data || {};
      toast.success(`Dispatcher ran — seeded ${seeded}, sent ${sent}, failed ${failed}, no-response ${no_response}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual run failed");
    } finally { setDispatching(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]">
        <AdminSidebar />
        <main className="lg:ml-64 p-6 md:p-10 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
        </main>
      </div>
    );
  }

  const emailOff = status && status.email_enabled === false;
  const smsOff = status && status.sms_enabled === false;

  return (
    <div className="min-h-screen bg-[#FAF8F0]" data-testid="trial-reminder-settings-page">
      <AdminSidebar />
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-3xl">
        <div className="mb-8">
          <p className="text-[10px] tracking-widest uppercase text-[#9A7B4F] mb-1">Settings</p>
          <h1 className="font-serif text-3xl text-[#282828]">Trial reminders</h1>
          <p className="text-sm text-[#676664] mt-2 leading-relaxed max-w-xl">
            Configure who receives admin copies of trial reminders and attendance
            responses. The reminder cadence and dispatch engine are untouched — the
            fields below only tune inputs the engine already reads.
          </p>
        </div>

        {/* Channel health banner */}
        <div className="grid gap-3 mb-6">
          {emailOff && (
            <div className="border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                 data-testid="warn-resend-missing">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-sm text-amber-900 font-medium">Email reminders are currently disabled</p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  <b>RESEND_API_KEY</b> is not configured on the backend. Reminder emails
                  and attendance notifications by email will be skipped until the key is
                  added to <code>backend/.env</code>. <b>SMS is unaffected</b> and will
                  continue to send via ClickSend.
                </p>
              </div>
            </div>
          )}
          {smsOff && (
            <div className="border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                 data-testid="warn-clicksend-missing">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-sm text-amber-900 font-medium">SMS reminders are currently disabled</p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  ClickSend credentials are missing. Add <b>CLICKSEND_USERNAME</b> and
                  <b> CLICKSEND_API_KEY</b> to <code>backend/.env</code> to re-enable SMS.
                </p>
              </div>
            </div>
          )}
          {!emailOff && !smsOff && (
            <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3"
                 data-testid="channels-ok">
              <Check className="w-5 h-5 text-emerald-700 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-emerald-900">Both email (Resend) and SMS (ClickSend) channels are active.</p>
            </div>
          )}
        </div>

        {/* Channel indicators */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <ChannelChip label="Email · Resend" ok={!emailOff} icon={Mail} />
          <ChannelChip label="SMS · ClickSend" ok={!smsOff} icon={MessageSquare} />
        </div>

        {/* Form */}
        <div className="bg-white border border-[#282828]/10 p-6 space-y-6">
          {/* Enable */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
              className="mt-1 accent-[#9A7B4F]"
              data-testid="reminder-enabled-toggle"
            />
            <div>
              <p className="text-sm text-[#282828] font-medium">Enable reminder dispatcher</p>
              <p className="text-xs text-[#676664] mt-0.5">When off, no reminders are seeded or sent, and no-response sweeps also pause.</p>
            </div>
          </label>

          {/* Emails */}
          <Field
            label="Admin email recipients"
            hint="Comma or newline separated. Reminder + attendance response copies go to each."
          >
            <textarea
              value={emails} onChange={e => setEmails(e.target.value)}
              rows={2} placeholder="ops@shopmahra.com.au, admin@shopmahra.com.au"
              className="w-full border border-[#282828]/15 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#9A7B4F]"
              data-testid="admin-emails-input"
            />
          </Field>

          {/* Phones */}
          <Field
            label="Admin mobile recipients"
            hint="Comma or newline separated. Australian numbers may start with 04 — they'll be normalised to +61…"
          >
            <textarea
              value={phones} onChange={e => setPhones(e.target.value)}
              rows={2} placeholder="0400 000 000, +61411222333"
              className="w-full border border-[#282828]/15 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#9A7B4F]"
              data-testid="admin-phones-input"
            />
          </Field>

          {/* Offsets */}
          <Field
            label="Reminder offsets (days before appointment)"
            hint="7 and 2 by default. Comma separated whole numbers between 1 and 60."
          >
            <Input
              value={offsets} onChange={e => setOffsets(e.target.value)}
              placeholder="7, 2"
              className="w-56 border-[#282828]/15 focus:border-[#9A7B4F]"
              data-testid="offsets-input"
            />
          </Field>

          {/* Send hour */}
          <Field
            label="Send time (Australia/Sydney)"
            hint="Reminders fire at this local hour on the offset day. 9 = 9:00 AM Sydney time."
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <Input
                type="number" min="0" max="23" value={sendHour}
                onChange={e => setSendHour(e.target.value)}
                className="w-24 border-[#282828]/15 focus:border-[#9A7B4F]"
                data-testid="send-hour-input"
              />
              <span className="text-sm text-[#676664]">:00 AEST/AEDT</span>
            </div>
          </Field>

          <div className="flex items-center gap-3 pt-2 border-t border-[#282828]/10">
            <Button
              onClick={save} disabled={saving}
              className="bg-[#9A7B4F] hover:bg-[#8A6D45] text-white uppercase tracking-widest text-xs"
              data-testid="save-settings-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" strokeWidth={1.5}/> Save settings</>}
            </Button>
            <Button
              variant="outline" onClick={runNow} disabled={dispatching}
              className="border-[#9A7B4F]/40 uppercase tracking-widest text-xs"
              data-testid="run-dispatcher-btn"
            >
              {dispatching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlayCircle className="w-4 h-4 mr-2" strokeWidth={1.5}/> Run dispatcher now</>}
            </Button>
          </div>
        </div>

        <p className="text-[10px] tracking-widest uppercase text-[#9A7B4F]/70 mt-6">
          Section 18 · Rev 3 — Settings only
        </p>
        </div>
      </main>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <p className="text-sm text-[#282828] font-medium mb-1">{label}</p>
      {hint ? <p className="text-xs text-[#676664] mb-2 leading-relaxed">{hint}</p> : null}
      {children}
    </div>
  );
}

function ChannelChip({ label, ok, icon: Icon }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 border ${ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
      <Icon className={`w-4 h-4 ${ok ? "text-emerald-700" : "text-red-600"}`} strokeWidth={1.5} />
      <span className={`text-xs ${ok ? "text-emerald-900" : "text-red-800"}`}>{label}</span>
      <span className={`ml-auto text-[10px] uppercase tracking-widest ${ok ? "text-emerald-700" : "text-red-700"}`}>
        {ok ? <span className="flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={1.5}/> Active</span>
            : <span className="flex items-center gap-1"><X className="w-3 h-3" strokeWidth={1.5}/> Disabled</span>}
      </span>
    </div>
  );
}
